import { useEffect, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { useRequireSuperAdmin } from '../../lib/useRequireSuperAdmin';
import { api, type BackupStatus, type SuperStats } from '../../lib/api';
import { relativeTime } from '../../lib/dashboardHelpers';

interface StatTileProps {
  label: string;
  value: string | number;
  hint?: string;
}

function StatTile({ label, value, hint }: StatTileProps) {
  return (
    <div
      className="bg-surface"
      style={{
        padding: 20,
        border: '1px solid var(--color-hairline)',
        borderRadius: 16,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div
        className="font-semibold uppercase mb-2"
        style={{ fontSize: 11, color: 'var(--color-ink-muted)', letterSpacing: '0.06em' }}
      >
        {label}
      </div>
      <div
        className="font-display font-bold text-ink tabular-nums"
        style={{ fontSize: 30, letterSpacing: '-0.02em', lineHeight: 1 }}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {hint && (
        <div className="text-xs text-ink-muted mt-1.5">{hint}</div>
      )}
    </div>
  );
}

export default function AdminStatsPage() {
  const gate = useRequireSuperAdmin();
  const [stats, setStats] = useState<SuperStats | null>(null);
  const [backup, setBackup] = useState<BackupStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gate.ready) return;
    Promise.all([api.superStats(), api.superBackupStatus()])
      .then(([s, b]) => {
        setStats(s);
        setBackup(b);
      })
      .catch(err => setError((err as Error).message || 'Failed to load stats'));
  }, [gate.ready]);

  if (!gate.ready) return null;

  return (
    <AdminShell title="System stats" subtitle="Platform-wide health and growth metrics.">
      {error && (
        <div
          className="bg-surface mb-6"
          style={{
            padding: 16,
            border: '1px solid var(--color-hairline)',
            borderRadius: 14,
            color: 'var(--color-peach-deep)',
          }}
        >
          {error}
        </div>
      )}

      {!stats ? (
        <div className="text-ink-soft" style={{ padding: 40 }}>Loading…</div>
      ) : (
        <>
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 14,
              marginBottom: 24,
            }}
          >
            <StatTile label="Total users" value={stats.totalUsers} hint={`+${stats.signupsThisWeek} this week`} />
            <StatTile label="Total workspaces" value={stats.totalWorkspaces} />
            <StatTile label="Total links" value={stats.totalLinks} hint={`+${stats.linksThisWeek} this week`} />
            <StatTile label="Total clicks" value={stats.totalClicks} hint={`+${stats.clicksThisWeek} this week`} />
            <StatTile label="Signups this week" value={stats.signupsThisWeek} />
            <StatTile label="Signups this month" value={stats.signupsThisMonth} />
          </div>

          <div
            className="bg-surface"
            style={{
              padding: 24,
              border: '1px solid var(--color-hairline)',
              borderRadius: 16,
              boxShadow: 'var(--shadow-sm)',
            }}
          >
            <h2
              className="font-display font-bold text-ink m-0 mb-3"
              style={{ fontSize: 18 }}
            >
              Backups
            </h2>
            {!backup ? (
              <div className="text-ink-soft text-sm">Loading…</div>
            ) : (
              <div className="text-sm" style={{ display: 'grid', gap: 8 }}>
                <Row label="Last backup at">
                  {backup.lastBackupAt
                    ? `${relativeTime(backup.lastBackupAt)} (${backup.lastBackupAt})`
                    : '—'}
                </Row>
                <Row label="Last backup status">
                  <span
                    className="font-mono"
                    style={{
                      padding: '2px 8px',
                      borderRadius: 6,
                      fontSize: 12,
                      background:
                        backup.lastBackupStatus === 'ok'
                          ? 'var(--color-mint)'
                          : backup.lastBackupStatus
                          ? 'var(--color-peach)'
                          : 'var(--color-bg-alt)',
                      color:
                        backup.lastBackupStatus === 'ok'
                          ? 'var(--color-mint-deep)'
                          : backup.lastBackupStatus
                          ? 'var(--color-peach-deep)'
                          : 'var(--color-ink-soft)',
                    }}
                  >
                    {backup.lastBackupStatus ?? 'never'}
                  </span>
                </Row>
                <Row label="R2 configured">
                  <span
                    className="font-mono"
                    style={{
                      padding: '2px 8px',
                      borderRadius: 6,
                      fontSize: 12,
                      background: backup.r2Configured ? 'var(--color-mint)' : 'var(--color-peach)',
                      color: backup.r2Configured
                        ? 'var(--color-mint-deep)'
                        : 'var(--color-peach-deep)',
                    }}
                  >
                    {backup.r2Configured ? 'yes' : 'no'}
                  </span>
                </Row>
              </div>
            )}
          </div>
        </>
      )}
    </AdminShell>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center gap-3">
      <span className="text-ink-soft">{label}</span>
      <span className="text-ink">{children}</span>
    </div>
  );
}
